import React, {Component} from 'react';
import MediumPost from './blogPost';
import {Col, Row, Typography, Button, Spin} from 'antd';
import styled, {keyframes} from 'styled-components';
import {ArrowRightOutlined, LoadingOutlined} from '@ant-design/icons';

const {Title, Paragraph} = Typography;

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Styled Components
const NewsContainer = styled.section`
  padding: ${props => props.theme.spacing?.[20] || '5rem'}
    ${props => props.theme.spacing?.[6] || '1.5rem'};
  background: ${props => props.theme.colors?.neutral?.[900] || '#171717'};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 30% 80%, rgba(226, 39, 132, 0.1) 0%, transparent 50%);
    pointer-events: none;
  }

  @media (max-width: ${props => props.theme.breakpoints?.md || '768px'}) {
    padding: ${props => props.theme.spacing?.[12] || '3rem'}
      ${props => props.theme.spacing?.[4] || '1rem'};
  }
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: ${props => props.theme.spacing?.[16] || '4rem'};
  animation: ${fadeInUp} 1s ease-out;
`;

const SectionTitle = styled(Title)`
  && {
    font-family: ${props =>
      props.theme.typography?.fontFamily?.heading ||
      "'Hanson Bold', 'Inter', system-ui, sans-serif"};
    font-size: ${props => props.theme.typography?.fontSize?.['4xl'] || '2.25rem'};
    font-weight: ${props => props.theme.typography?.fontWeight?.black || '900'};
    color: ${props => props.theme.colors?.neutral?.[50] || '#fafafa'};
    margin-top: ${props => props.theme.spacing?.[8] || '2rem'};
    margin-bottom: ${props => props.theme.spacing?.[4] || '1rem'};

    @media (max-width: ${props => props.theme.breakpoints?.md || '768px'}) {
      font-size: ${props => props.theme.typography?.fontSize?.['3xl'] || '1.875rem'};
      margin-top: ${props => props.theme.spacing?.[6] || '1.5rem'};
    }
  }
`;

const SectionSubtitle = styled(Paragraph)`
  && {
    font-size: ${props => props.theme.typography?.fontSize?.lg || '1.125rem'};
    color: ${props => props.theme.colors?.neutral?.[400] || '#a3a3a3'};
    max-width: 600px;
    margin: 0 auto;
    line-height: ${props => props.theme.typography?.lineHeight?.relaxed || '1.625'};
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${props => props.theme.spacing?.[16] || '4rem'};
  color: ${props => props.theme.colors?.neutral?.[400] || '#a3a3a3'};
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${props => props.theme.spacing?.[16] || '4rem'};
  color: ${props => props.theme.colors?.neutral?.[400] || '#a3a3a3'};
  text-align: center;
`;

const ViewMoreButton = styled(Button)`
  && {
    background: transparent;
    border: 2px solid ${props => props.theme.colors?.secondary?.[500] || '#f97316'};
    border-radius: ${props => props.theme.borderRadius?.full || '9999px'};
    padding: ${props => props.theme.spacing?.[4] || '1rem'}
      ${props => props.theme.spacing?.[8] || '2rem'};
    height: auto;
    font-family: ${props =>
      props.theme.typography?.fontFamily?.heading ||
      "'Hanson Bold', 'Inter', system-ui, sans-serif"};
    font-size: ${props => props.theme.typography?.fontSize?.lg || '1.125rem'};
    font-weight: ${props => props.theme.typography?.fontWeight?.semibold || '600'};
    color: ${props => props.theme.colors?.secondary?.[500] || '#f97316'};
    margin-top: ${props => props.theme.spacing?.[12] || '3rem'};
    transition: all ${props => props.theme.animation?.duration?.normal || '300ms'}
      ${props => props.theme.animation?.easing?.ease || 'cubic-bezier(0.4, 0, 0.2, 1)'};

    &:hover,
    &:focus {
      background: ${props => props.theme.colors?.secondary?.[500] || '#f97316'};
      color: white;
      transform: translateY(-2px);
      box-shadow: ${props =>
        props.theme.boxShadow?.lg ||
        '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'};
    }

    .anticon {
      margin-left: ${props => props.theme.spacing?.[2] || '0.5rem'};
    }
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  animation: ${fadeInUp} 1s ease-out 0.6s both;
`;

const urlForFeedToJson = feed => `https://api.rss2json.com/v1/api.json?rss_url=${feed}`;

// Fallback blog posts when RSS feed is not available
const fallbackPosts = [
  {
    title: 'Building Your Creative Portfolio: A Guide for College Students',
    description:
      'Learn how to create a compelling portfolio that showcases your creative skills and attracts brand partnerships.',
    link: 'https://medium.com/cllctve',
    pubDate: '2024-01-15',
    thumbnail: null,
    content: 'A comprehensive guide to building an outstanding creative portfolio...',
  },
  {
    title: 'The Future of Creator Economy: Trends to Watch',
    description:
      'Explore the latest trends shaping the creator economy and how college creatives can leverage them.',
    link: 'https://medium.com/cllctve',
    pubDate: '2024-01-10',
    thumbnail: null,
    content: 'The creator economy is evolving rapidly, and understanding these trends...',
  },
  {
    title: 'Brand Partnerships 101: What Creatives Need to Know',
    description: 'Everything you need to know about working with brands as a college creative.',
    link: 'https://medium.com/cllctve',
    pubDate: '2024-01-05',
    thumbnail: null,
    content: 'Brand partnerships can be a game-changer for college creatives...',
  },
  {
    title: 'Social Media Strategy for Gen-Z Creators',
    description:
      'Discover effective social media strategies tailored for the next generation of creators.',
    link: 'https://medium.com/cllctve',
    pubDate: '2024-01-01',
    thumbnail: null,
    content: 'Social media is the playground for Gen-Z creators, and mastering it...',
  },
];

class Widget extends Component {
  constructor(props) {
    super(props);
    this.state = {
      requestFailed: false,
      active: 0,
      mediumPosts: null,
    };
  }

  componentDidMount() {
    // Only fetch if we have a valid RSS feed link
    if (this.props.mediumRSSFeedLink) {
      this.fetchMediumPosts();
    } else {
      // If no RSS feed, use fallback posts
      this.setState({
        mediumPosts: fallbackPosts,
      });
    }
  }

  fetchMediumPosts = () => {
    fetch(urlForFeedToJson(this.props.mediumRSSFeedLink))
      .then(response => {
        if (!response.ok) {
          throw Error('Network request failed');
        }
        return response.json();
      })
      .then(data => {
        const dataItems = data.items;
        if (dataItems && dataItems.length > 0) {
          this.setState({
            mediumPosts: dataItems,
          });
        } else {
          // Use fallback posts if no items returned
          this.setState({
            mediumPosts: fallbackPosts,
          });
        }
      })
      .catch(() => {
        // Use fallback posts instead of showing error
        this.setState({
          mediumPosts: fallbackPosts,
        });
      });
  };

  render() {
    const {requestFailed, mediumPosts} = this.state;

    return (
      <NewsContainer>
        <ContentWrapper>
          <SectionHeader>
            <SectionTitle level={2}>Latest News & Insights</SectionTitle>
            <SectionSubtitle>
              Stay updated with the latest trends, success stories, and insights from the CLLCTVE
              community.
            </SectionSubtitle>
          </SectionHeader>

          {requestFailed && (
            <ErrorContainer>
              <Paragraph style={{fontSize: '1.125rem', marginBottom: '1rem'}}>
                Unable to load latest articles at the moment.
              </Paragraph>
              <Paragraph>
                Visit our Medium publication for the latest updates and insights.
              </Paragraph>
            </ErrorContainer>
          )}

          {!mediumPosts && !requestFailed && (
            <LoadingContainer>
              <Spin
                indicator={<LoadingOutlined style={{fontSize: 48, color: '#f97316'}} spin />}
                size="large"
              />
              <Paragraph style={{marginTop: '1rem', fontSize: '1.125rem'}}>
                Loading latest articles...
              </Paragraph>
            </LoadingContainer>
          )}

          {mediumPosts && mediumPosts.length > 0 && (
            <>
              <Row gutter={[32, 32]} justify="center">
                {mediumPosts.slice(0, 4).map((post, index) => (
                  <Col xs={24} sm={12} lg={6} key={index}>
                    <MediumPost post={post} index={index} />
                  </Col>
                ))}
              </Row>

              <ButtonContainer>
                <ViewMoreButton
                  size="large"
                  onClick={() => window.open('https://medium.com/cllctve', '_blank')}
                  icon={<ArrowRightOutlined />}
                >
                  View More Articles
                </ViewMoreButton>
              </ButtonContainer>
            </>
          )}

          {mediumPosts && mediumPosts.length === 0 && (
            <ErrorContainer>
              <Paragraph style={{fontSize: '1.125rem', marginBottom: '1rem'}}>
                No articles available at the moment.
              </Paragraph>
              <Paragraph>Check back soon for the latest updates and insights.</Paragraph>
            </ErrorContainer>
          )}
        </ContentWrapper>
      </NewsContainer>
    );
  }
}

export default Widget;
